import { useEffect, useState, useRef } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { uploadClientFileChunked } from "@/lib/upload";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Paperclip, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PAYMENT = [{ v: "unpaid", l: "Unpaid" }, { v: "paid", l: "Paid" }, { v: "reimbursed", l: "Reimbursed" }];
const inputCls = "w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]";
const labelCls = "block text-sm font-semibold text-gray-700 mb-1";
const todayStr = () => new Date().toISOString().slice(0, 10);

export default function CostFormDialog({ open, onOpenChange, clientId, cost, categories = [], onSaved }) {
  const [date, setDate] = useState(todayStr());
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("Other");
  const [description, setDescription] = useState("");
  const [vendor, setVendor] = useState("");
  const [billable, setBillable] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState("unpaid");
  const [fileId, setFileId] = useState(null);
  const [attachmentName, setAttachmentName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (cost) {
      setDate(cost.date || todayStr()); setAmount(String(cost.amount ?? ""));
      setCategory(cost.category || "Other"); setDescription(cost.description || "");
      setVendor(cost.vendor || ""); setBillable(cost.billable ?? true);
      setPaymentStatus(cost.payment_status || "unpaid");
      setFileId(cost.file_id || null); setAttachmentName(cost.attachment?.filename || "");
    } else {
      setDate(todayStr()); setAmount(""); setCategory(categories[0] || "Other");
      setDescription(""); setVendor(""); setBillable(true); setPaymentStatus("unpaid");
      setFileId(null); setAttachmentName("");
    }
  }, [open, cost, categories]);

  const uploadInvoice = async (file) => {
    if (!file) return;
    const MAX = 100 * 1024 * 1024;
    if (file.size > MAX) { toast.error("File is too large (max 100 MB)."); return; }
    const CHUNK = 1024 * 1024;
    const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const total = Math.max(1, Math.ceil(file.size / CHUNK));
    setUploading(true);
    try {
      let last = null;
      for (let i = 0; i < total; i++) {
        const blob = file.slice(i * CHUNK, (i + 1) * CHUNK);
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("chunk_index", i);
        fd.append("total_chunks", total);
        fd.append("filename", file.name);
        fd.append("content_type", file.type || "application/octet-stream");
        fd.append("doc_type", "cost_invoice");
        fd.append("chunk", blob, file.name);
        const { data } = await api.post(`/clients/${clientId}/files/chunk`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (data.done) last = data;
      }
      if (last?.id) { setFileId(last.id); setAttachmentName(file.name); toast.success("Invoice attached"); }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Invoice upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt < 0) { toast.error("Enter a valid amount"); return; }
    if (!description.trim()) { toast.error("Add a short description"); return; }
    setSaving(true);
    const body = { date, amount: amt, category, description, vendor, billable,
      payment_status: paymentStatus, file_id: fileId };
    try {
      if (cost) await api.put(`/costs/${cost.id}`, body);
      else await api.post(`/clients/${clientId}/costs`, body);
      toast.success(cost ? "Cost updated" : "Cost added");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save cost");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg rounded-sm max-h-[90vh] overflow-y-auto" data-testid="cost-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{cost ? "Edit cost" : "Add cost"}</DialogTitle>
          <DialogDescription className="sr-only">Record a case cost with an optional invoice attachment.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className={labelCls}>Date</label>
            <input type="date" data-testid="cost-date-input" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Amount (USD)</label>
            <input type="number" min="0" step="0.01" data-testid="cost-amount-input" value={amount}
              onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className={inputCls} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className={labelCls}>Category</label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="cost-category-trigger" className="rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className={labelCls}>Payment status</label>
            <Select value={paymentStatus} onValueChange={setPaymentStatus}>
              <SelectTrigger data-testid="cost-payment-trigger" className="rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{PAYMENT.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <label className={labelCls}>Description</label>
        <input data-testid="cost-description-input" value={description} onChange={(e) => setDescription(e.target.value)}
          placeholder="e.g. Court filing fee — complaint" className={inputCls + " mb-3"} />

        <label className={labelCls}>Vendor / Payee</label>
        <input data-testid="cost-vendor-input" value={vendor} onChange={(e) => setVendor(e.target.value)}
          placeholder="e.g. County Clerk" className={inputCls + " mb-3"} />

        <label className="flex items-center gap-2 text-sm text-gray-700 mb-4" data-testid="cost-billable-toggle">
          <input type="checkbox" checked={billable} onChange={(e) => setBillable(e.target.checked)} className="accent-[#002FA7] h-4 w-4" />
          Billable to client (reimbursable)
        </label>

        <label className={labelCls}>Invoice / supporting document (optional)</label>
        {attachmentName ? (
          <div className="flex items-center gap-2 border border-gray-200 rounded-sm px-3 py-2 text-sm bg-gray-50" data-testid="cost-attachment-chip">
            <Paperclip className="w-4 h-4 text-teal-600 shrink-0" />
            <span className="flex-1 truncate text-gray-700">{attachmentName}</span>
            <button type="button" onClick={() => { setFileId(null); setAttachmentName(""); }} data-testid="cost-remove-attachment"
              className="text-gray-400 hover:text-red-600"><X className="w-4 h-4" /></button>
          </div>
        ) : (
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="cost-attach-button"
            className="flex items-center gap-2 border border-dashed border-gray-300 rounded-sm px-3 py-2 text-sm text-gray-600 hover:border-[#002FA7] hover:text-[#002FA7] w-full justify-center disabled:opacity-60">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
            {uploading ? "Uploading…" : "Attach invoice"}
          </button>
        )}
        <input ref={fileRef} type="file" className="hidden" data-testid="cost-file-input"
          onChange={(e) => uploadInvoice(e.target.files?.[0])} />

        <DialogFooter className="mt-5">
          <button onClick={() => onOpenChange(false)} className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-sm font-semibold text-sm">Cancel</button>
          <button onClick={save} disabled={saving || uploading} data-testid="cost-save-button"
            className="bg-[#002FA7] hover:bg-[#002480] text-white px-4 py-2 rounded-sm font-semibold text-sm disabled:opacity-60">
            {saving ? "Saving…" : cost ? "Save changes" : "Add cost"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
